import React, { useCallback } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DefaultButton } from '@fluentui/react';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getGtmInstructionsStyles } from './gtm-instructions-styles';
import { getExpressionOperatorOptions, getValueOperatorOptions } from '../../../components/goal-creation-options';
import imageStep3 from '../../assets/add-new-tag-gtm.png';
import imageStep4 from '../../assets/step4Image.png';
import imageStep5EventGoal from '../../assets/step5ImageEventGoal.png';
import imageStep5DestinationGoal from '../../assets/step5ImageDestinationGoal.png';
import imageStep5ProductGoal from '../../assets/step5ImageProductGoal.png';

const GtmSetupInstructionsViewUnstyled = (props) => {
  const {
    classes,
    i18n,
    type,
    context,
  } = props;
  const noTranslateModel = { noTranslate: text => (<b>{text}</b>) };
  const step3aTemplate = i18n.getString(_TL_('1. Go to {{noTranslate}}Tags{{/noTranslate}}.'));
  const step3bTemplate = i18n.getString(_TL_('2. Click {{noTranslate}}New{{/noTranslate}}.'));
  const step4Template = i18n.getString(_TL_('Under Tag Configuration, select {{noTranslate}}choose a tag type to begin setup{{/noTranslate}}.'));
  const step5Template = i18n.getString(_TL_('Search for {{noTranslate}}Microsoft Advertising Universal Event Tracking{{/noTranslate}} and then update tag configuration.'));
  const step6Template = i18n.getString(_TL_('Click {{noTranslate}}Submit{{/noTranslate}} and then {{noTranslate}}publish{{/noTranslate}} the changes.'));

  const handleClick = useCallback(() => {
    window.open(
      'https://tagmanager.google.com/', // TODO: replace with account ID url
      '_blank' // <- This is what makes it open in a new window.
    );
  }, []);

  const getExpressionOpI18n = useCallback((opName) => {
    let i18nName;
    _.each(getExpressionOperatorOptions(i18n), (option) => {
      if (opName === option.key) {
        i18nName = option.text;
      }
    });
    return i18nName;
  }, [i18n]);

  const getValueOpI18n = useCallback((opName) => {
    let i18nName;
    _.each(getValueOperatorOptions(i18n), (option) => {
      if (opName === option.key) {
        i18nName = option.text;
      }
    });
    return i18nName;
  }, [i18n]);

  return (
    <div className={classNames(classes.container, 'GTM-instructions')}>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 1: Access your Google Tag Manager account'))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <p>{i18n.getString(_TL_('Click Update Google Tag Manager account to open your Google Tag Manager account.'))}</p>
          <DefaultButton onClick={handleClick}>{i18n.getString(_TL_('Update Google Tag Manager account'))}</DefaultButton>
        </div>
      </div>
      {type !== 'DestinationGoal' && (
        <div className={classNames(classes.instructionContainer)}>
          <div className={classNames(classes.instructionTitle)}>
            <p>
              {`${i18n.getString(_TL_('Step 2: Go to the Google Tag Manager'))} `}
              <span><b>{i18n.getString(_TL_('account'))}</b>{context.gtmAccountName ? ` (${context.gtmAccountName})` : ''}</span>
              {` ${i18n.getString(_TL_('and'))} `}
              <span><b>{i18n.getString(_TL_('container'))}</b>{context.gtmContainerName ? ` (${context.gtmContainerName})` : ''}</span>
              {` ${i18n.getString(_TL_('you selected in the previous steps'))}`}
            </p>
          </div>
        </div>
      )}
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>
            {`${type === 'DestinationGoal' ? i18n.getString(_TL_('Step 2:')) : i18n.getString(_TL_('Step 3:'))} ${i18n.getString(_TL_('Add a new tag to your container'))}`}
          </p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep3} alt={i18n.getString(_TL_('Add a new tag to your container'))} />
          </div>
          <p>{i18n.getString(_TL_('Make sure are logged into your Google Tag Manager account and use the same account and container selected previously.'))}</p>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={step3aTemplate}
            />
          </p>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={step3bTemplate}
            />
          </p>
        </div>
      </div>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>
            {`${type === 'DestinationGoal' ? i18n.getString(_TL_('Step 3:')) : i18n.getString(_TL_('Step 4:'))} `}
            <ReactTemplate
              model={noTranslateModel}
              template={step4Template}
            />
          </p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep4} alt={i18n.getString(_TL_('Under Tag Configuration, select choose a tag type to begin setup'))} />
          </div>
        </div>
      </div>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>
            {`${type === 'DestinationGoal' ? i18n.getString(_TL_('Step 4:')) : i18n.getString(_TL_('Step 5:'))} `}
            <ReactTemplate
              model={noTranslateModel}
              template={step5Template}
            />
          </p>
        </div>
        {type === 'EventGoal' &&
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep5EventGoal} alt={i18n.getString(_TL_('tag configuration for custom event'))} />
          </div>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('1. Set {{noTranslate}}track type{{/noTranslate}} as {{noTranslate}}Custom conversion{{/noTranslate}}'))} /></p>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('2. Define {{noTranslate}}event action{{/noTranslate}}, {{noTranslate}}event category{{/noTranslate}}, {{noTranslate}}event label{{/noTranslate}} and {{noTranslate}}event value{{/noTranslate}} based on what is needed for your goal definition. You can also use your Google Tag Manager variables to define these parameters.'))} /></p>
          <p>{i18n.getString(_TL_('Custom event used by this conversion goal'))}</p>
          <table className={classNames(classes.eventParamTable, 'event-param-table')}>
            <tbody>
              <tr>
                <th>{i18n.getString(_TL_('Custom event parameters'))}</th>
                <th>{i18n.getString(_TL_('Qualifier'))}</th>
                <th>{i18n.getString(_TL_('Value'))}</th>
              </tr>
              {context.eventParams.Action &&
                <tr>
                  <td>Event action</td>
                  <td>{getExpressionOpI18n(context.eventParams.ActionOperator)}</td>
                  <td>{context.eventParams.Action}</td>
                </tr>
              }
              {context.eventParams.Category &&
                <tr>
                  <td>Event category</td>
                  <td>{getExpressionOpI18n(context.eventParams.CategoryOperator)}</td>
                  <td>{context.eventParams.Category}</td>
                </tr>
              }
              {context.eventParams.Label &&
                <tr>
                  <td>Event label</td>
                  <td>{getExpressionOpI18n(context.eventParams.LabelOperator)}</td>
                  <td>{context.eventParams.Label}</td>
                </tr>
              }
              {context.eventParams.Value &&
                <tr>
                  <td>Event value</td>
                  <td>{getValueOpI18n(context.eventParams.ValueOperator)}</td>
                  <td>{context.eventParams.Value}</td>
                </tr>
              }
            </tbody>
          </table>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('3. Set the right {{noTranslate}}currency{{/noTranslate}} based on your account. Assign {{noTranslate}}Revenue value{{/noTranslate}} to the right variable which captures the value for this conversion.'))} /></p>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('4. Assign the {{noTranslate}}Trigger{{/noTranslate}} for when this conversion should happen on your website.'))} /></p>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('5. Click {{noTranslate}}Save{{/noTranslate}}.'))} /></p>
        </div>
        }
        {type === 'DestinationGoal' &&
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep5DestinationGoal} alt={i18n.getString(_TL_('tag configuration for variable revenue'))} />
          </div>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('1. Set {{noTranslate}}track type{{/noTranslate}} as {{noTranslate}}Variable revenue for destination URL{{/noTranslate}}'))} /></p>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('2. Set the right {{noTranslate}}currency{{/noTranslate}} based on your account. Assign {{noTranslate}}Revenue value{{/noTranslate}} to the right variable which captures the value for this conversion.'))} /></p>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('3. Assign the {{noTranslate}}Trigger{{/noTranslate}} for when this conversion should happen on your website.'))} /></p>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('4. Click {{noTranslate}}Save{{/noTranslate}}.'))} /></p>
        </div>
        }
        {type === 'ProductConversionGoal' &&
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep5ProductGoal} alt={i18n.getString(_TL_('tag configuration for product conversion goal'))} />
          </div>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('1. Set {{noTranslate}}track type{{/noTranslate}} as {{noTranslate}}Vertical: Ecommerce{{/noTranslate}}'))} /></p>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('2. Set the value of {{noTranslate}}Retail product ID{{/noTranslate}} to the variable which contains the product IDs.'))} /></p>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('3. Set the value of {{noTranslate}}Retail page type{{/noTranslate}} as either \'purchase\' or dynamically as the variable which contains the value of page type.'))} /></p>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('4. Set the right {{noTranslate}}currency{{/noTranslate}} based on your account. Assign {{noTranslate}}Revenue value{{/noTranslate}} to the right variable which captures the value for this conversion.'))} /></p>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('5. Assign the {{noTranslate}}Trigger{{/noTranslate}} for when this conversion should happen on your website.'))} /></p>
          <p><ReactTemplate model={noTranslateModel} template={i18n.getString(_TL_('6. Click {{noTranslate}}Save{{/noTranslate}}.'))} /></p>
        </div>
        }
      </div>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>
            {`${type === 'DestinationGoal' ? i18n.getString(_TL_('Step 5:')) : i18n.getString(_TL_('Step 6:'))} `}
            <ReactTemplate
              model={noTranslateModel}
              template={step6Template}
            />
          </p>
        </div>
      </div>
    </div>
  );
};

GtmSetupInstructionsViewUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getGtmInstructionsStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  type: PropTypes.string.isRequired,
  context: PropTypes.shape({
    gtmAccountId: PropTypes.string.isRequired,
    gtmAccountName: PropTypes.string.isRequired,
    gtmContainerName: PropTypes.string.isRequired,
    eventParams: PropTypes.shape({}).isRequired,
  }).isRequired,
};

export const GtmSetupInstructionsView = withDefaultStyles(GtmSetupInstructionsViewUnstyled, getGtmInstructionsStyles);
